import { useEffect, useState } from "react";
import { Link } from "wouter";
import type { User } from "../lib/types";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { I } from "../components/Icons";
import { overallPct } from "../lib/step-totals";

interface ManagedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  providers: string[];
  createdAt: number | null;
  progress: {
    steps: Record<string, string[]>;
    quiz: Record<string, number>;
  };
}

interface Props {
  user: User;
  onSignOut: () => void;
}

const PROVIDER_LABELS: Record<string, string> = {
  "microsoft-entra-id": "Microsoft",
  credential: "Local",
};

function methodLabels(providers: string[]): string[] {
  if (!providers.length) return ["—"];
  return providers.map((p) => PROVIDER_LABELS[p] ?? p);
}

function formatCreated(ts: number | null): string {
  if (!ts) return "—";
  // better-auth stores the timestamp as Unix seconds via the drizzle adapter;
  // normalise to ms before formatting (tolerate either just in case).
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function Users({ user, onSignOut }: Props) {
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [localAuth, setLocalAuth] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      if (!res.ok) {
        setError(
          res.status === 403
            ? "You don't have access to this page."
            : `Failed to load (${res.status}).`,
        );
        return;
      }
      const data = (await res.json()) as {
        users: ManagedUser[];
        localAuth: boolean;
      };
      setUsers(data.users);
      setLocalAuth(data.localAuth);
      setError(null);
    } catch {
      setError("Couldn't reach the server.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="gf-shell">
      <Sidebar active="users" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "Team progress", href: "/admin" },
            { t: "Manage users", strong: true },
          ]}
        />
        <div className="gf-dash">
          <div className="gf-dash__section-head">
            <div>
              <h2>Manage users</h2>
              <p>
                Everyone who's signed in — via Microsoft or a local account — with
                their sign-in method and overall progress. Create local learner
                accounts below to walk a course as a test user.
              </p>
            </div>
            <Link href="/admin" className="gf-btn gf-btn--secondary">
              <I.Eye size={15} /> Team progress
            </Link>
          </div>

          <CreateLocalUser
            localAuth={localAuth}
            onCreated={() => void load()}
          />

          {error ? (
            <div
              className="gf-card"
              style={{ padding: "var(--space-5)", color: "var(--danger)" }}
            >
              {error}
            </div>
          ) : !users ? (
            <div
              className="gf-card"
              style={{ padding: "var(--space-5)", color: "var(--fg-3)" }}
            >
              Loading…
            </div>
          ) : users.length === 0 ? (
            <div
              className="gf-card"
              style={{ padding: "var(--space-5)", color: "var(--fg-3)" }}
            >
              No one has signed in yet.
            </div>
          ) : (
            <UsersTable users={users} />
          )}
        </div>
      </div>
    </div>
  );
}

function CreateLocalUser({
  localAuth,
  onCreated,
}: {
  localAuth: boolean;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(null);
    setBusy(true);
    try {
      const res = await fetch("/api/admin/users", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        user?: { email: string };
      };
      if (!res.ok) {
        setErr(data.error ?? `Failed to create user (${res.status}).`);
        return;
      }
      setOk(`Created ${data.user?.email ?? email}. They can now sign in locally.`);
      setName("");
      setEmail("");
      setPassword("");
      onCreated();
    } catch {
      setErr("Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gf-card" style={{ padding: "var(--space-5)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <I.Plus size={16} />
        <h3 style={{ margin: 0, fontSize: "var(--fs-h4)" }}>Add a local learner</h3>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: "var(--fs-body-sm)", color: "var(--fg-2)" }}>
        Creates an email + password account so you can complete a course as a test
        learner, then review their progress here as an admin.
      </p>

      {!localAuth && (
        <div
          style={{
            fontSize: "var(--fs-body-sm)",
            color: "var(--fg-2)",
            background: "var(--bg-3)",
            border: "1px solid var(--border-2)",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 16,
          }}
        >
          Local authentication is currently <b>off</b>. You can still create accounts,
          but they won't be able to sign in until <code>GITFIT_LOCAL_AUTH</code> is
          turned on.
        </div>
      )}

      <form
        onSubmit={submit}
        style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}
      >
        <label className="gf-field" style={{ flex: "1 1 180px" }}>
          <span>Name</span>
          <input
            className="gf-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Test Learner"
            autoComplete="off"
            required
          />
        </label>
        <label className="gf-field" style={{ flex: "1 1 220px" }}>
          <span>Email</span>
          <input
            className="gf-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="learner@example.com"
            autoComplete="off"
            required
          />
        </label>
        <label className="gf-field" style={{ flex: "1 1 180px" }}>
          <span>Password</span>
          <input
            className="gf-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            minLength={8}
            autoComplete="new-password"
            required
          />
        </label>
        <button
          type="submit"
          className="gf-btn gf-btn--primary"
          disabled={busy}
          style={{ flex: "0 0 auto" }}
        >
          {busy ? "Creating…" : "Create learner"}
        </button>
      </form>

      {err && (
        <div className="gf-login__error" style={{ marginTop: 12 }}>
          {err}
        </div>
      )}
      {ok && (
        <div
          style={{
            marginTop: 12,
            fontSize: "var(--fs-body-sm)",
            color: "var(--success)",
            background: "var(--success-bg, rgba(16,185,129,0.1))",
            padding: "8px 10px",
            borderRadius: 6,
          }}
        >
          {ok}
        </div>
      )}
    </div>
  );
}

function UsersTable({ users }: { users: ManagedUser[] }) {
  const th: React.CSSProperties = {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "var(--fs-caption)",
    color: "var(--fg-3)",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "var(--ls-wide)",
    borderBottom: "1px solid var(--border-2)",
    whiteSpace: "nowrap",
  };
  const td: React.CSSProperties = {
    padding: "12px",
    borderBottom: "1px solid var(--border-1)",
    fontSize: "var(--fs-body-sm)",
    verticalAlign: "middle",
  };

  return (
    <div
      className="gf-card"
      style={{ padding: 0, overflowX: "auto", marginTop: "var(--space-5)" }}
    >
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
        <thead>
          <tr>
            <th style={th}>User</th>
            <th style={th}>Sign-in method</th>
            <th style={{ ...th, textAlign: "center" }}>Role</th>
            <th style={{ ...th, textAlign: "center" }}>Progress</th>
            <th style={th}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const overall = Math.round(overallPct(u.progress) * 100);
            return (
              <tr key={u.id}>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: "var(--fg-1)" }}>{u.name}</div>
                  <div style={{ fontSize: "var(--fs-caption)", color: "var(--fg-3)" }}>
                    {u.email}
                  </div>
                </td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {methodLabels(u.providers).map((m) => (
                      <MethodChip key={m} label={m} />
                    ))}
                  </div>
                </td>
                <td style={{ ...td, textAlign: "center" }}>
                  <RoleChip role={u.role} />
                </td>
                <td
                  style={{
                    ...td,
                    textAlign: "center",
                    fontWeight: 600,
                    color: overall >= 100 ? "var(--success)" : overall > 0 ? "var(--fg-1)" : "var(--fg-3)",
                  }}
                >
                  {overall}%
                </td>
                <td style={{ ...td, color: "var(--fg-2)", whiteSpace: "nowrap" }}>
                  {formatCreated(u.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MethodChip({ label }: { label: string }) {
  const isLocal = label === "Local";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "var(--fs-caption)",
        fontWeight: 600,
        background: isLocal ? "rgba(0,24,71,0.06)" : "rgba(0,120,212,0.1)",
        color: isLocal ? "var(--fg-2)" : "#0067b8",
        border: "1px solid var(--border-1)",
      }}
    >
      {label}
    </span>
  );
}

function RoleChip({ role }: { role: string }) {
  const isAdmin = role === "admin";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "var(--fs-caption)",
        fontWeight: 600,
        textTransform: "capitalize",
        background: isAdmin ? "var(--nymbl-gradient-45)" : "transparent",
        color: isAdmin ? "#fff" : "var(--fg-3)",
        border: isAdmin ? "0" : "1px solid var(--border-2)",
      }}
    >
      {role || "user"}
    </span>
  );
}
